import { type EligibilityConditions } from "src/generated/hooks/form-service/requests";
import { z } from "zod";

type Comparison = NonNullable<
  EligibilityConditions["expression"]
>["anyOf"][number]["allOf"][number];
type AddressComparison = Extract<Comparison, { kind: "Address" }>;

export type AddressOperator = "within" | "notWithin";

export type AddressCondition = {
  kind: "address";
  id: string;
  boundaryId: string;
  operator: AddressOperator | "";
  dataPointId: string;
};

export function emptyAddressCondition(): AddressCondition {
  return {
    kind: "address",
    id: crypto.randomUUID(),
    boundaryId: "",
    operator: "",
    dataPointId: "",
  };
}

const requiredMessage = "This field is required";

export const addressConditionSchema = z.object({
  kind: z.literal("address"),
  id: z.string(),
  boundaryId: z.string().min(1, requiredMessage),
  operator: z.string().min(1, requiredMessage),
  dataPointId: z.string().min(1, requiredMessage),
});

function readAddressOperator(operator: string): AddressOperator | null {
  if (operator === "Within") return "within";
  if (operator === "NotWithin") return "notWithin";
  return null;
}

export function comparisonToAddressCondition(
  comparison: Comparison
): AddressCondition | null {
  if (comparison.kind !== "Address") return null;
  const operator = readAddressOperator(comparison.operator);
  if (!operator) return null;

  return {
    kind: "address",
    id: crypto.randomUUID(),
    boundaryId: comparison.boundary.id,
    operator,
    dataPointId: comparison.address.id,
  };
}

export function addressConditionToComparison(
  condition: AddressCondition
): AddressComparison {
  if (
    (condition.operator !== "within" && condition.operator !== "notWithin") ||
    condition.boundaryId === "" ||
    condition.dataPointId === ""
  ) {
    throw new Error("Address condition is incomplete");
  }

  return {
    kind: "Address",
    operator: condition.operator === "within" ? "Within" : "NotWithin",
    address: {
      source: "Reference",
      id: condition.dataPointId,
      type: "Address",
    },
    boundary: { source: "Boundary", id: condition.boundaryId },
  };
}

export function describeAddressCondition(
  condition: AddressCondition,
  dataPoints: Map<string, string>,
  boundaries: Map<string, string>
): string {
  const address = dataPoints.get(condition.dataPointId) ?? "Unknown data point";
  const boundary = boundaries.get(condition.boundaryId) ?? "Unknown boundary";
  if (condition.operator === "within" || condition.operator === "notWithin") {
    const relation =
      condition.operator === "within" ? "is inside" : "is outside";
    return `${address} ${relation} ${boundary}`;
  }
  return address;
}
